import { useCallback, useRef, useState } from "react";

export interface RunExclusiveParams<T> {
  task: () => Promise<T>;
}

export interface SubmitGuard {
  isSubmitting: boolean;
  /** Resolve com o retorno da task, ou `undefined` se já havia envio em voo. */
  runExclusive: <T>(params: RunExclusiveParams<T>) => Promise<T | undefined>;
}

/**
 * Trava de envio único para blocos com captura de lead. A trava é um ref porque
 * `isSubmitting` só muda no re-render, e o handler ainda espera o token do
 * antibot antes do POST — nessa janela um guard de state deixa passar (ATW-426).
 */
export const useSubmitGuard = (): SubmitGuard => {
  const inFlightRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const runExclusive = useCallback(
    async <T>({ task }: RunExclusiveParams<T>): Promise<T | undefined> => {
      if (inFlightRef.current) return undefined;

      inFlightRef.current = true;
      setIsSubmitting(true);

      try {
        return await task();
      } finally {
        inFlightRef.current = false;
        setIsSubmitting(false);
      }
    },
    [],
  );

  return { isSubmitting, runExclusive };
};
